<template>
  <div class="v-select-wrapper w-full">
    <v-select
      ref="select"
      :class="[
        getLabel ? 'v-select--with-label' : '',
        prependInnerIcon ? 'v-select--with-prepend-icon' : '',
        multiple && (someItemsSelected || allItemsSelected)
          ? 'v-select--items-selected'
          : '',
      ]"
      v-bind="commonProps"
      :filled="filled"
      :multiple="multiple"
      :chips="chips"
      deletable-chips
      :small-chips="smallChips"
      :item-text="getItemText"
      :item-value="getItemValue"
      :items="items || choices"
      @change="change"
      @input="update"
      @mousedown="mouseDown"
      persistent-placeholder
      :menu-props="{ bottom: true, offsetY: true }"
      :append-icon="'mdi-chevron-down'"
      :disabled="disabled"
      :prepend-inner-icon="multiple ? 'mdi-plus' : prependInnerIcon"
      :hide-details="'auto'"
      :loading="dataLoading"
      :return-object="returnObject"
    >
      <template v-slot:label>
        {{ getLabel }}<span v-if="required" class="required__icon">*</span>
        <slot name="info"></slot>
      </template>

      <template
        v-if="!hideSelectAll && multiple && choices.length"
        v-slot:prepend-item
      >
        <v-list-item
          :class="allItemsSelected ? 'v-list-item--active' : ''"
          ripple
          @mousedown.prevent
          @click="toggle"
        >
          <v-list-item-action>
            <v-icon
              :color="allItemsSelected || someItemsSelected ? 'primary' : ''"
            >
              {{ icon }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title> Select all </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-if="multiple" v-slot:selection></template>
    </v-select>
    <template v-if="multiple">
      <div
        :class="[
          'd-flex',
          'flex-wrap',
          'selection-chip__wrapper',
          selectedItems.length ? 'selection-chip__wrapper--has-selection' : '',
        ]"
      >
        <template v-if="!allItemsSelected">
          <template v-for="item in selectedItems">
            <v-chip
              class="selection-chip"
              :key="`${item[getItemValue] || item}-key`"
            >
              <span class="pr-2">
                {{ item[getItemText] || item }}
              </span>
              <v-icon small @click="$refs['select'].selectItem(item)"
                >mdi-close</v-icon
              >
            </v-chip>
          </template>
        </template>
        <template v-else-if="choices.length && allItemsSelected">
          <v-chip class="selection-chip" :key="`all-selected-key`">
            <span class="pr-2">
              All
              <span class="text-lowercase">{{ getLabel }}</span>
            </span>
            <v-icon small @click="toggle">mdi-close</v-icon>
          </v-chip>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import Input from "../../../mixins/input";
import Multiple from "../../../mixins/multiple";
import Editable from "../../../mixins/editable";
import ReferenceInput from "../../../mixins/reference-input";

/**
 * Value editing from a fixed choices. Support multiple and references.
 * If no choices, by default, takes localized enums with source as value from your VueI18n resources locales.
 */
export default {
  mixins: [Input, Multiple, Editable, ReferenceInput],
  props: {
    /**
     * Prop only valid when used inside of va-array input.
     * Determines if only unique values can be selected across all the selects that are inside a single
     * va-array input
     */
    uniqueChoice: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prependInnerIcon: {
      type: String,
      default: "",
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    dataLoading: {
      type: Boolean,
      default: false,
    },
    hideSelectAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      itemsCopy: [],
    };
  },
  watch: {
    input: {
      handler() {
        this.hideAlreadySelected();
      },
    },
  },
  async created() {
    this.items = await this.fetchChoices();
    this.itemsCopy = this.items;
    this.hideAlreadySelected();
  },
  mounted() {
    if (this.multiple) {
      this.$refs["select"]?.$el &&
        this.$refs["select"]?.$el
          .querySelector(".v-select__selections")
          .setAttribute("data-placeholder", this.placeholder);
    }
  },
  methods: {
    mouseDown() {
      this.hideAlreadySelected();
    },
    hideAlreadySelected() {
      if (!this.uniqueChoice) return false;
      if (!this.$attrs?.arrayInput) return false;
      const items = this.itemsCopy;
      const hiddenItems = this.$attrs.selected;
      this.items = items.filter((item) => {
        return !hiddenItems.some((hiddenItem) => {
          return (
            hiddenItem[this.source || this.getItemValue] ===
              item[this.getItemValue] &&
            hiddenItem[this.source || this.getItemValue] !== this.input
          );
        });
      });
    },
  },
};
</script>
